<template>
  <div class="add-card-modal">
    <div class="card-meta">
      <h2 class="mb-2">Billing and Invoice</h2>

      <VAlert v-if="trialEnd" :value="true" type="info">
        You are currently in a free trial period.
        <template v-if="inUpgradeProcess">
          If you add a card and don't complete the upgrade steps,
        </template>
        <template v-else>
          If you add a card now,
        </template>
        you will be migrated to an equivalent paying plan when the trial ends (on {{ trialEnd }}) at
        ${{ plan.amount / 100 }} / {{ plan.interval }} (excluding tax).
      </VAlert>

      <VForm @submit.prevent="addCard">
        <div class="card personal-info px-3 py-1 pb-2">
          <div class="card-information">
            <h3>Card Information</h3>
            <p class="mt-2 mb-0">Name of Cardholder:</p>
            <VTextField
              id="fullname"
              class="mt-0 mb-0"
              style="padding-top: 3px;"
              v-model="cardInfos.name"
              name="fullname"
              autofocus
              :rules="[() => cardInfos.name.length > 0 || 'This field is required']"
              autocomplete="name"
              />
            <p class="mt-1 mb-0">Card Information:</p>

            <div ref="card" />
          </div>

          <div class="card-information mt-2">
            <h3>Invoice Information</h3>

            <VTextField
              v-model="companyName"
              name="companyName"
              label="Company name"
              autocomplete="organization"
            />

            <VTextField
              v-model="cardInfos.address_line1"
              name="address"
              label="Address Line 1"
              :rules="[() => cardInfos.address_line1.length > 0 || 'This field is required']"
              required
              autocomplete="address-line1"
            />

            <VTextField
              v-model="cardInfos.address_line2"
              name="address"
              label="Address Line 2"
              autocomplete="address-line2"
            />

            <div class="input-row">
              <div class="mr-3">
                <VTextField v-model="cardInfos.address_zip" name="zip" label="Zip Code" />
              </div>

              <div class="mr-3">
                <VTextField
                  v-model="cardInfos.address_city"
                  name="city"
                  label="City"
                  :rules="[() => cardInfos.address_city.length > 0 || 'This field is required']"
                  required
                />
              </div>

              <div>
                <VAutocomplete
                  v-model="cardInfos.address_country"
                  :items="countries"
                  itemText="name"
                  itemValue="name"
                  label="Country"
                  :rules="[() => cardInfos.address_country.length > 0 || 'This field is required']"
                  required
                  @keydown.enter.native.prevent
                />
              </div>
            </div>
          </div>

          <div v-if="isVATrequired" class="card-information mt-2">
            <h3>VAT Identification Number</h3>
            As you are in Europe please put your company VAT number to avoid paying extra taxes. You
            can use a service <a href="https://vat-search.eu" target="_blank">like this one</a> to
            find it easily.
            <VTextField v-model="vatNumber" value="Label Text" name="vatNumber" label="VAT id" />
          </div>

          <div class="cb-btn mt-3">
            <span v-if="!card.id">

              <VBtn type="submit" :disabled="addCardLoading" color="primary">
                <span v-if="!addCardLoading"> Next <VIcon small>mdi-arrow-right</VIcon> </span>
                <VProgressCircular v-else indeterminate color="white" />
              </VBtn>
            </span>
            <span v-else>
              <VBtn color="primary" @click="handleAddCard({})">
                Next
              </VBtn>
            </span>
          </div>
        </div>
      </VForm>
    </div>

    <div class="payment-footer mt-1">
      <i>Payment Powered by Stripe</i><br />
      <a href="https://stripe.com" title="Payment Powered By Stripe" target="_blank">
        <img width="200" src="@/assets/stripe-payment.png" />
      </a>
    </div>
  </div>
</template>

<script>
import * as act from '@/store/actions'
import axios from 'axios'
import { formatDate } from '@/utils/format'
import stripe from '@/utils/stripe'
import countries from './countries.json'

let card

export default {
  name: 'AddCardForm',
  components: {},
  props: {
    handleBack: Function,
    handleAddCard: {
      required: true,
      type: Function,
    },
    inUpgradeProcess: Boolean,
  },

  data() {
    return {
      card: {},
      countries: countries,
      isVATrequired: false,
      cardInfos: {
        address_city: '',
        address_country: '',
        address_line1: '',
        address_zip: '',
        name: '',
      },
      vatNumber: '',
      companyName: '',
      addCardLoading: false,
      subscription: null,
    }
  },
  computed: {
    trialEnd() {
      return (
        this.subscription &&
        this.subscription.status === 'trialing' &&
        formatDate(new Date(this.subscription.trial_end * 1000))
      )
    },
    plan() {
      return this.$store.getters.currentPlan
    },
  },
  watch: {
    'cardInfos.address_country': {
      handler(country_input) {
        this.isVATrequired = false
        countries.forEach(country => {
          if (country_input == country.name && country.vat) {
            this.isVATrequired = true
          }
        })
      },
    },
  },

  mounted() {
    const options = {
      hidePostalCode: true,
      style: {
        base: {
          color: 'white',
          fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
          fontSmoothing: 'antialiased',
          fontSize: '16px',
          '::placeholder': {
            color: '#aab7c4',
          },
        },
        invalid: {
          color: '#fa755a',
          iconColor: '#fa755a',
        },
      },
    }
    if (!card) {
      card = stripe()
        .elements()
        .create('card', options)
      card.mount(this.$refs.card)
    } else {
      card.update(options)
      card.unmount()
      card.mount(this.$refs.card)
    }

    this.$store.getters.currentBillingSubscription.then(subscription => {
      this.subscription = subscription
    })
  },

  methods: {
    goToPM2ActivatePopup() {
      this.$store.dispatch('ACTIVATE_PM2', {
        position: this.position,
      })
    },
    missingFields() {
      const c = this.cardInfos
      if (c.name === '') {
        return 'Missing your name'
      }
      if (c.address_city === '') {
        return 'City field missing'
      }
      if (c.address_country === '') {
        return 'Country field missing'
      }
      if (c.address_line1 === '') {
        return 'Address Street missing'
      }
      return null
    },
    async addCard() {
      const missingFields = this.missingFields()

      if (missingFields) {
        return this.$store.dispatch(act.DISPLAY_NOTIFICATION, {
          type: 'error',
          message: missingFields,
        })
      }

      this.addCardLoading = true

      try {
        const result = await stripe().createToken(card, this.cardInfos)
        if (result.error) {
          console.log(result)
          throw new Error(result.error.message)
        }
        await this.handleAddCard({
          vatNumber: this.isVATrequired ? this.vatNumber : '',
          companyName: this.companyName,
          card: result.token,
        })
        this.addCardLoading = false
      } catch (e) {
        this.addCardLoading = false
        return this.$store.dispatch(act.DISPLAY_NOTIFICATION, {
          type: 'error',
          message: e,
        })
      }
    },
  },
}
</script>

<style lang="postcss">
.card-information {
  border: 2px solid var(--base);
  border-radius: 5px;
  background-color: var(--contrast);
  box-sizing: border-box;
  padding: 10px 14px;
}

.StripeElement {
  padding: 10px;
  margin-top: 10px;
  border-bottom: 1px solid white;
}

.StripeElement--focus {
}

.StripeElement--invalid {
  border-color: #fa755a;
}

.StripeElement--webkit-autofill {
  background-color: #fefde5 !important;
}
</style>

<style lang="postcss" scoped>
.add-card-modal {
  padding: 0px;
  margin: 0 auto;
  border-radius: 8px;
  & h2 {
    margin-bottom: 5px;
  }
  & .cb-btn {
    text-align: center;
  }
  & .payment-footer {
    width: 100%;
    text-align: center;
  }

  & .input-row {
    display: flex;

    & > * {
      flex: 1;
    }
  }
}
</style>
